/**
 * PaesImage — renderer for PAES stimulus images.
 *
 * Contract:
 *   - Never crops (object-fit: contain, width: 100%, height: auto).
 *   - Supports multiple images via imagen_refs = "a.png|b.png" (order preserved).
 *   - imagen_asset_path may carry the same "|" separator with full paths.
 *   - Click any image to open a full-screen modal ("Ver imagen completa").
 *   - imagen_alt is used as accessible alt text.
 *
 * Props:
 *   item: {
 *     imagen_refs?: string,             // "a.png" or "a.png|b.png"
 *     imagen_asset_path?: string,       // "/assets/.../a.png" or "|"-separated
 *     imagen_alt?: string,
 *   }
 *
 * Renders nothing when no image is present (safe null-guard).
 */
import { useState } from "react";

const DEFAULT_BASE = "/assets/reto-avanzo/paes";

function resolvePaths(item) {
  if (!item) return [];
  const refs = String(item.imagen_refs || "").split("|").map(s => s.trim()).filter(Boolean);
  if (!refs.length) return [];
  const paths = String(item.imagen_asset_path || "").split("|").map(s => s.trim()).filter(Boolean);
  if (paths.length === refs.length) return paths;
  // Fallback: build paths from refs against the default public base.
  return refs.map(r => `${DEFAULT_BASE}/${r}`);
}

export default function PaesImage({ item }) {
  const [zoom, setZoom] = useState(null);
  const paths = resolvePaths(item);
  if (!paths.length) return null;
  const alt = item?.imagen_alt || "Imagen del reactivo";

  return (
    <>
      <div
        data-testid="paes-image-block"
        style={{ display: "flex", flexDirection: "column", gap: 12, margin: "12px 0" }}
      >
        {paths.map((src, i) => (
          <figure
            key={src + i}
            style={{ margin: 0, width: "100%" }}
            data-testid={`paes-image-fig-${i}`}
          >
            <img
              src={src}
              alt={alt}
              onClick={() => setZoom(src)}
              style={{
                width: "100%",
                height: "auto",
                maxWidth: "100%",
                objectFit: "contain",
                display: "block",
                cursor: "zoom-in",
                borderRadius: 8,
                background: "#fff",
              }}
              data-testid={`paes-image-${i}`}
            />
          </figure>
        ))}
        <button
          type="button"
          onClick={() => setZoom(paths[0])}
          data-testid="paes-image-zoom-btn"
          style={{
            alignSelf: "flex-start",
            padding: "6px 10px",
            fontSize: 13,
            background: "transparent",
            border: "1px solid #cbd5e1",
            borderRadius: 6,
            cursor: "pointer",
          }}
        >
          Ver imagen completa
        </button>
      </div>

      {zoom && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setZoom(null)}
          data-testid="paes-image-modal"
          style={{
            position: "fixed", inset: 0, background: "rgba(15,23,42,0.85)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 9999, padding: 24, cursor: "zoom-out",
          }}
        >
          <img
            src={zoom}
            alt={alt}
            style={{
              maxWidth: "95vw",
              maxHeight: "95vh",
              width: "auto",
              height: "auto",
              objectFit: "contain",
              background: "#fff",
              borderRadius: 8,
            }}
            data-testid="paes-image-modal-img"
          />
        </div>
      )}
    </>
  );
}
